import React, { useEffect, useRef, useState } from 'react';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
  isLnatPearsonExam,
  isEsatPearsonExam,
  isTaraPearsonExam,
  getPearsonPlayerExamTitle,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import {
  TmuaPearsonInstructionPage,
  TmuaPearsonInstructionTable,
  NextNCopy,
} from './TmuaPearsonInstructionCopy';
import { buildLnatSectionBInstructionBody } from '../utils/lnatSectionBInstructionCopy';

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TmuaInstructionsScreen = ({
  examName = 'TMUA',
  onNext,
  onEndExam,
  modeChoice,
  colorScheme = 'pearsonDefault',
  setColorScheme,
  variant = 'paper1',
  title,
  questionCount = 20,
  durationMinutes = 75,
  autoAdvance = false,
  sectionLabel,
  timeCell,
  bodyCopy,
  tsaSitting = false,
  heritageExam,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [showEndExam, setShowEndExam] = useState(false);
  const [showReadyToBegin, setShowReadyToBegin] = useState(false);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const T = TMUA_PEARSON_TOKENS;
  const isLnat = isLnatPearsonExam(examName);
  const isEsat = isEsatPearsonExam(examName);
  const isTara = isTaraPearsonExam(examName);
  const nextVariant = isLnat || isEsat || isTara ? 'chrome' : 'yellow';
  const nextIconColor = isLnat || isEsat || isTara ? '#FFFFFF' : T.nextDefaultFg;
  const timedAdvance = Boolean(autoAdvance && modeChoice === 'timed');
  const defaultPaperLabel = variant === 'paper2' ? 'Paper 2' : 'Paper 1';
  const paperLabel = sectionLabel
    || (isLnat
      ? (variant === 'paper2' ? 'Section B : Essay' : 'Section A : Multiple Choice')
      : defaultPaperLabel);
  const count = isLnat
    ? (Number(questionCount) || 0)
    : (Number(questionCount) > 0 ? Number(questionCount) : 20);
  const minutes = Number(durationMinutes) || 75;
  const resolvedTimeCell = timeCell || (modeChoice === 'timed' ? `${minutes} minutes` : 'Untimed');
  const lnatReadyAfterNext = isLnat && variant === 'paper2';
  const instructionBody = bodyCopy
    || (isLnat && variant === 'paper2' ? buildLnatSectionBInstructionBody(count) : null);

  useEffect(() => {
    if (!timedAdvance) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (lnatReadyAfterNext) setShowReadyToBegin(true);
          else onNextRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timedAdvance, lnatReadyAfterNext]);

  const handleNext = () => {
    if (lnatReadyAfterNext) {
      setShowReadyToBegin(true);
      return;
    }
    onNext();
  };

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExam,
      showReadyToBeginVisible: showReadyToBegin,
      nextAvailable: !showEndExam && !showReadyToBegin,
      endExamAvailable: !showReadyToBegin,
    },
    {
      onNext: () => { if (!showEndExam && !showReadyToBegin) handleNext(); },
      onEndExam: () => setShowEndExam(true),
      onConfirmYes: () => {
        if (showReadyToBegin) {
          setShowReadyToBegin(false);
          onNext();
        } else if (showEndExam) onEndExam();
      },
      onConfirmNo: () => {
        if (showReadyToBegin) setShowReadyToBegin(false);
        else setShowEndExam(false);
      },
    }
  );

  return (
    <TmuaPearsonShell
      examName={isLnat || isEsat || isTara ? examName : 'TMUA'}
      title={title || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}
      tsaSitting={tsaSitting}
      heritageExam={heritageExam}
      colorSchemeLabel={isLnat ? getPearsonColorSchemeDropdownLabel(examName) : undefined}
      onBackToSelection={onEndExam}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      timeRemainingLabel={timedAdvance ? formatCountdown(secondsLeft) : null}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={() => setShowEndExam(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <TmuaPearsonFooterButton variant={nextVariant} onClick={handleNext} icon={<NextIcon color={nextIconColor} />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <TmuaPearsonInstructionPage>
        {timedAdvance ? (
          <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#000' }}>
            You have 1 minute to read these instructions.
          </p>
        ) : null}
        <>
          <p style={{ margin: '0 0 12px', color: 'var(--iv-body)' }}>{paperLabel}</p>
          <TmuaPearsonInstructionTable
            rows={[{ section: paperLabel, questions: count, time: resolvedTimeCell }]}
          />
          {Array.isArray(instructionBody) ? instructionBody.map((para, idx) => (
            <p key={idx} style={{ margin: '0 0 12px', color: 'var(--iv-body)', fontWeight: para.bold ? 700 : 400 }}>
              {para.text}
            </p>
          )) : null}
          <NextNCopy />
        </>
      </TmuaPearsonInstructionPage>
      {showReadyToBegin ? (
        <TmuaPearsonDialog
          title="Ready to Begin"
          info
          onYes={() => { setShowReadyToBegin(false); onNext(); }}
          onNo={() => setShowReadyToBegin(false)}
        >
          Are you ready to begin Section B?
        </TmuaPearsonDialog>
      ) : null}
      {showEndExam ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={onEndExam}
          onNo={() => setShowEndExam(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
};

export default TmuaInstructionsScreen;
